import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle,
  Clock,
  MapPin,
  Phone,
  Mail,
  Download,
  ArrowLeft,
  Home,
  Info,
} from "lucide-react";
import { OrderStatusNotification } from "../components/OrderStatusNotification";
import { Footer } from "../components/Footer";
import { useApp } from "@/App";
import { getOrderDetails } from "@/services/menuService";
import { formatCanadianPhoneNumber } from "@/services/utlits";
import apiConfig from "@/configs/apiConfig";

export function OrderConfirmationPage() {
  const { restaurant } = useApp();
  const { orderId } = useParams();
  const [orderStatus, setOrderStatus] = useState("confirmed");
  const [showNotification, setShowNotification] = useState(false);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [openTaxToolTip, setOpenTaxToolTip] = useState(false);

  // Simulate order status updates with 10-second delays for recording
  // useEffect(() => {
  //   const statusUpdates = [
  //     { status: "confirmed", delay: 0 },
  //     { status: "preparing", delay: 10000 }, // 10 seconds
  //     { status: "cooking", delay: 20000 }, // 20 seconds total
  //     { status: "ready", delay: 30000 }, // 30 seconds total
  //     { status: "delivered", delay: 40000 }, // 40 seconds total
  //   ];

  //   statusUpdates.forEach(({ status, delay }) => {
  //     setTimeout(() => {
  //       setOrderStatus(status);
  //       setShowNotification(true);
  //     }, delay);
  //   });
  // }, []);

  // Auto refresh order status every 10 seconds UNTIL delivered
  useEffect(() => {
    if (orderId) {
      const interval = setInterval(async () => {
        const res = await getOrderDetails(orderId);
        if (res) {
          setOrderDetails(res);
          setOrderStatus(res?.status);

          // Stop polling when delivered or cancelled
          if (
            res?.status?.toLowerCase() === "delivered" ||
            res?.status?.toLowerCase() === "out for delivery" ||
            res?.status?.toLowerCase() === "ready for pickup" ||
            res?.status?.toLowerCase() === "ready for delivery" ||
            res?.status?.toLowerCase() === "cancelled"
          ) {
            clearInterval(interval);
          }
        }
      }, 10000); // 10 seconds

      return () => clearInterval(interval);
    } else {
      console.log("Order ID not found");
    }
  }, [orderId]);

  const getStatusInfo = (status: string) => {
    switch (status) {
      case "Pending":
      case "pending":
        return {
          text: "Order Placed",
          color: "bg-emerald-500",
          description: "We're confirming your order.",
        };
      case "preparing":
      case "Preparing":
        return {
          text: "Preparing Your Food",
          color: "bg-yellow-500",
          description: "Chef is preparing your food",
        };
      case "ready for pickup":
      case "ready for delivery":
        return {
          text: "Order is Ready",
          color: "bg-green-500",
          description:
            orderDetails?.orderMode == "pickup"
              ? "Your order is ready for pickup!"
              : "Your order is ready for delivery!",
        };
      case "delivered":
      case "Out for Delivery":
      case "out for delivery":
        return {
          text: "Out for Delivery",
          color: "bg-blue-500",
          description: "Order is out for delivery!",
        };
      case "cancelled":
      case "Cancelled":
        return {
          text: "Order Cancelled",
          color: "bg-blue-500",
          description: "Your order has been cancelled.",
        };
      default:
        return {
          text: "Processing",
          color: "bg-gray-500",
          description: "Processing your order",
        };
    }
  };

  const currentStatus = getStatusInfo(orderStatus);
  const lowercaseStatus = orderStatus?.toLowerCase();
  const isPickup = orderDetails?.orderMode?.toLowerCase() === "pickup";
  const mainBranch = restaurant?.branches?.find((b) => b.isMainBranch);
  const openMap = () => {
    console.log("sec", mainBranch);

    if (!mainBranch) return;
    const { latitude, longitude } = mainBranch;
    console.log("sec", latitude, longitude);
    window.open(
      `https://www.google.com/maps?q=${latitude},${longitude}`,
      "_blank",
    );
  };
  const steps = [
    {
      id: "placed",
      text: "Order placed",
      completed: true,
    },
    {
      id: "confirmed",
      text: "Order confirmed",
      completed: [
        "preparing",
        "ready for pickup",
        "ready for delivery",
        "out for delivery",
        "delivered",
      ].includes(lowercaseStatus),
    },
    {
      id: "preparing",
      text: "Preparing your food",
      completed: [
        "preparing",
        "ready for pickup",
        "ready for delivery",
        "out for delivery",
        "delivered",
      ].includes(lowercaseStatus),
    },
    isPickup
      ? {
          id: "ready_for_pickup",
          text: "Order is ready for pickup",
          completed: ["ready for pickup"].includes(lowercaseStatus),
        }
      : {
          id: "ready_for_delivery",
          text: "Order is ready for delivery",
          completed: [
            "ready for delivery",
            "out for delivery",
            "delivered",
          ].includes(lowercaseStatus),
        },
    !isPickup && {
      id: "out_for_delivery",
      text: "Out for delivery",
      completed: ["out for delivery", "delivered"].includes(lowercaseStatus),
    },
  ].filter(Boolean); // removes `false` items when pickup mode is active

  const getStatusMessage = (
    status: string,
    time: number,
  ): { label: string; message: string } => {
    const lowerStatus = status?.toLowerCase();

    switch (lowerStatus) {
      case "pending":
        return {
          label: "Estimated Ready Time",
          message: "Processing",
        };
      case "preparing":
        return {
          label: "Estimated Ready Time",
          message: `${Math.max(time - 15, 0)} - ${time} minutes`,
        };
      case "ready for pickup":
        return {
          label: "Pickup Status",
          message: "Your order is ready to be picked up at the counter.",
        };
      case "ready for delivery":
        return {
          label: "Delivery Status",
          message: "Your order is ready and will be dispatched shortly.",
        };
      case "out for delivery":
        return {
          label: "Estimated Delivery Time",
          message: `${Math.max(time - 15, 0)} - ${time} minutes`,
        };
      default:
        return {
          label: "Order Status",
          message: "Please check back shortly.",
        };
    }
  };
  const { label, message } = getStatusMessage(
    orderDetails?.status,
    orderDetails?.preparationTime,
  );
  type OrderStatus =
    | "pending"
    | "preparing"
    | "ready for pickup"
    | "ready for delivery"
    | "out for delivery"
    | "delivered"
    | "cancelled"
    | "default";

  // 2. Define the map with that type
  const statusDisplayMap: Record<
    OrderStatus,
    { title: string; contentMessage: string }
  > = {
    pending: {
      title: "Placed",
      contentMessage:
        "Thank you for your order. We're confirming it with the restaurant.",
    },
    preparing: {
      title: "Confirmed",
      contentMessage: "We're preparing your delicious food!",
    },
    "ready for pickup": {
      title: "Ready for Pickup",
      contentMessage: "Your order is ready! Please pick it up at the counter.",
    },
    "ready for delivery": {
      title: "Ready for Delivery",
      contentMessage: "Your order is ready and will be dispatched shortly.",
    },
    "out for delivery": {
      title: "Out for Delivery",
      contentMessage: "Your order is on the way. Sit tight!",
    },
    delivered: {
      title: "Delivered",
      contentMessage: "Your order has been delivered. Enjoy your meal!",
    },
    cancelled: {
      title: "Cancelled",
      contentMessage:
        "Your order has been cancelled. A refund will be processed within 3 working days.",
    },
    default: {
      title: "Update",
      contentMessage: "Your order status has been updated.",
    },
  };
  const statusKey = (orderStatus?.toLowerCase() as OrderStatus) || "default";
  const { title, contentMessage } =
    statusDisplayMap[statusKey] ?? statusDisplayMap.default;

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-thai-50 py-6 sm:py-8 animate-fade-in-up">
        <div className="max-w-5xl mx-auto mobile-padding">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 space-y-4 sm:space-y-0 animate-slide-in-down">
            <Link to="/">
              <Button
                variant="ghost"
                className="flex items-center space-x-2 transition-all duration-300 hover:-translate-x-1"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back to Menu</span>
                <span className="sm:hidden">Back</span>
              </Button>
            </Link>
            <Link to="/profile">
              <Button
                variant="outline"
                className="w-full sm:w-auto transition-all duration-300 hover:scale-105"
              >
                View All Orders
              </Button>
            </Link>
          </div>
          {/* Email Confirmation Notice */}
          {orderStatus?.toLowerCase() === "pending" ||
          orderStatus?.toLowerCase() === "preparing" ? (
            <div className="mt-6 sm:mt-8 mb-6 animate-slide-in-up">
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4 sm:p-6 text-center">
                  <div className="text-blue-800 text-sm sm:text-base">
                    {orderStatus?.toLowerCase() === "pending" ? (
                      <>
                        📧 You will receive a confirmation email once your order
                        is confirmed.
                      </>
                    ) : (
                      <>
                        📧 A confirmation email has been sent to your email
                        address with order details and tracking information.
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : null}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-4 sm:space-y-6 animate-slide-in-left">
              {/* Order Confirmation */}
              <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm animate-zoom-in">
                {/* <CardHeader className="text-center pb-4 sm:pb-6">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce-gentle">
                    <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </div>
                  <CardTitle className="text-2xl sm:text-3xl font-bold text-gray-900 font-display mb-2">
                    Order{" "}
                    {orderStatus?.toLowerCase() === "pending"
                      ? "Placed"
                      : orderStatus?.toLowerCase() === "preparing"
                      ? "Confirmed"
                      : orderStatus ?? ""}
                    !
                  </CardTitle>
                  <p className="text-gray-600 text-base sm:text-lg">
                    {orderStatus?.toLowerCase() === "cancelled"
                      ? "Your order has been cancelled. A refund will be processed within 3 working days."
                      : orderStatus?.toLowerCase() === "pending"
                      ? "Thank you for your order. We're confirming it with the restaurant."
                      : orderStatus?.toLowerCase() === "out for delivery"
                      ? "Your order is out for the delivery"
                      : "Thank you for your order. We're preparing your delicious food!"}
                  </p>
                </CardHeader> */}

                <CardHeader className="text-center pb-4 sm:pb-6">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce-gentle">
                    <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </div>
                  <CardTitle className="text-2xl sm:text-3xl font-bold text-gray-900 font-display mb-2">
                    Order {title}
                  </CardTitle>
                  <p className="text-gray-600 text-base sm:text-lg">
                    {contentMessage}
                  </p>
                </CardHeader>

                <CardContent className="space-y-4 sm:space-y-6">
                  {/* Order Details */}
                  <div className="bg-emerald-50 p-4 sm:p-6 rounded-xl border border-emerald-200 animate-glow">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      {/* Order Number */}
                      <div>
                        <span className="text-emerald-700 font-medium text-sm sm:text-base">
                          Order Number
                        </span>
                        <div className="font-bold text-emerald-800 text-xl sm:text-2xl">
                          {orderId}
                        </div>
                      </div>

                      {/* Total Amount + Paid/Unpaid Badge */}
                      {orderDetails && (
                        <div>
                          <span className="text-emerald-700 font-medium text-sm sm:text-base">
                            Total Amount
                          </span>

                          <div className="flex items-center gap-3 mt-1">
                            <div className="font-bold text-emerald-800 text-xl sm:text-2xl">
                              ${orderDetails?.total?.toFixed(2) ?? 0}
                            </div>

                            {/* Payment Status Badge */}
                            {orderDetails?.paymentMethod != "pay-later" ? (
                              <span className="px-2 py-1 text-xs font-semibold bg-emerald-600 text-white rounded-full">
                                Paid
                              </span>
                            ) : (
                              <span className="px-2 py-1 text-xs font-semibold bg-red-500 text-white rounded-full">
                                Unpaid
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Order Status */}
                  <div className="space-y-4">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 font-display">
                      Order Status
                    </h3>

                    {/* Current Status */}
                    <div className="bg-white p-4 sm:p-6 rounded-xl border-2 border-emerald-200 shadow-sm animate-pulse-slow">
                      <div className="flex items-center space-x-4">
                        <div
                          className={`w-4 h-4 ${currentStatus.color} rounded-full animate-pulse`}
                        ></div>
                        <div>
                          <div className="font-bold text-base sm:text-lg text-gray-900">
                            {currentStatus.text}
                          </div>
                          <div className="text-gray-600 text-sm sm:text-base">
                            {currentStatus.description}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Status Timeline */}
                    <div className="space-y-3">
                      {steps.map((step: any, index) => (
                        <div
                          key={step.id}
                          className="flex items-center space-x-3 animate-slide-in-right"
                          style={{ animationDelay: `${index * 0.1}s` }}
                        >
                          <div
                            className={`w-3 h-3 rounded-full transition-all duration-500 ${
                              step.completed
                                ? "bg-emerald-500 animate-pulse-slow"
                                : "bg-gray-300"
                            }`}
                          ></div>
                          <span
                            className={`text-sm sm:text-base ${
                              step.completed
                                ? "text-gray-900 font-medium"
                                : "text-gray-500"
                            }`}
                          >
                            {step.text}
                          </span>
                          {step.completed && (
                            <Badge className="bg-emerald-100 text-emerald-800 text-xs animate-zoom-in">
                              Completed
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Delivery Info */}
                  {orderDetails?.orderMode == "delivery" && (
                    <div className="space-y-4">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 font-display">
                        Delivery Information
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center space-x-3 p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200 animate-slide-in-up">
                          <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                          <div>
                            <div className="font-semibold text-blue-900 text-sm sm:text-base">
                              {label}
                            </div>
                            <div className="text-blue-700 text-sm">
                              {message}
                            </div>
                          </div>
                        </div>

                        <div
                          className="flex items-center space-x-3 p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200 animate-slide-in-up"
                          style={{ animationDelay: "0.1s" }}
                        >
                          <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                          <div>
                            <div className="font-semibold text-gray-900 text-sm sm:text-base">
                              Delivery Location
                            </div>
                            <div className="text-gray-700 text-sm">
                              {orderDetails?.shippingAddress?.addressLine1}
                            </div>
                            <div className="text-gray-700 text-sm">
                              {orderDetails?.shippingAddress?.city},{" "}
                              {orderDetails?.shippingAddress?.province}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {orderDetails?.orderMode == "pickup" && (
                    <div className="space-y-4">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 font-display">
                        Pickup Information
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center space-x-3 p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200 animate-slide-in-up">
                          <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                          <div>
                            <div className="font-semibold text-blue-900 text-sm sm:text-base">
                              {label}
                            </div>
                            <div className="text-blue-700 text-sm">
                              {message}
                            </div>
                          </div>
                        </div>

                        <div
                          onClick={openMap}
                          className="flex items-center space-x-3 p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100"
                        >
                          <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                          <div>
                            <div className="font-semibold text-gray-900 text-sm sm:text-base">
                              Pickup Location
                            </div>
                            <div className="text-gray-700 text-sm">
                              {restaurant?.name}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                    {/* <Button
                      variant="outline"
                      className="h-10 sm:h-12 transition-all duration-300 hover:scale-105"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Receipt
                    </Button> */}

                    <Link to="/">
                      <Button className="w-full h-10 sm:h-12 btn-primary">
                        <Home className="w-4 h-4 mr-2" />
                        Continue Shopping
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-4 sm:space-y-6 animate-slide-in-right">
              {/* Restaurant Info */}
              <Card className="animate-zoom-in">
                <CardHeader>
                  <CardTitle className="text-base sm:text-lg">
                    Restaurant Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm sm:text-base">
                      {restaurant?.name}
                    </h4>
                    <p className="text-gray-600 text-xs sm:text-sm">
                      {restaurant?.address1}
                    </p>
                    <p className="text-gray-600 text-xs sm:text-sm">
                      {restaurant?.address2}
                    </p>
                  </div>

                  <div className="space-y-2 text-xs sm:text-sm">
                    {restaurant?.phone && (
                      <div className="flex items-center space-x-2">
                        <Phone className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                        <span className="text-gray-700">
                          {formatCanadianPhoneNumber(restaurant?.phone || "")}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                      <span className="text-gray-700">
                        Open until {apiConfig.OPEN_TIMING} PM
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Order Items */}
              <Card
                className="animate-zoom-in"
                style={{ animationDelay: "0.1s" }}
              >
                <CardHeader>
                  <CardTitle className="text-base sm:text-lg">
                    Your Order
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2 text-xs sm:text-sm">
                    {orderDetails?.items?.map((item: any, index: number) => (
                      <div key={index} className="flex justify-between">
                        <span>
                          {item?.quantity}x {item?.name}
                        </span>
                        <span>
                          ${(item?.quantity * item?.price).toFixed(2)}
                        </span>
                      </div>
                    ))}
                    <div className="flex justify-between text-sm items-center">
                      <div className="flex items-center gap-1 relative">
                        <span>Taxes and Service fee</span>
                        <button
                          type="button"
                          onClick={() => setOpenTaxToolTip(!openTaxToolTip)}
                          onMouseEnter={() => setOpenTaxToolTip(true)}
                          onMouseLeave={() => setOpenTaxToolTip(false)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <Info size={18} />
                        </button>
                        {/* Tooltip */}
                        {openTaxToolTip && (
                          <div className="absolute top-6 left-0 z-50 w-52 rounded-md border bg-white p-3 shadow-lg text-xs">
                            {orderDetails?.orderTaxes?.map((tax: any) => (
                              <div
                                key={tax.name}
                                className="flex justify-between"
                              >
                                <span>
                                  {tax.name} ({tax.percentage}%)
                                </span>
                                <span>${tax.amount}</span>
                              </div>
                            ))}
                            <div className="flex justify-between">
                              <span>Service fee</span>
                              <span>${restaurant?.serviceFee?.toFixed(2)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                      {orderDetails && (
                        <span>
                          $
                          {(
                            orderDetails?.taxAmount + orderDetails?.serviceFee
                          )?.toFixed(2)}
                        </span>
                      )}
                    </div>
                    {orderDetails?.tipAmount != 0 && (
                      <div className="flex justify-between">
                        <span>Tip</span>
                        <span>${orderDetails?.tipAmount?.toFixed(2)}</span>
                      </div>
                    )}
                    {orderDetails?.orderMode === "delivery" && (
                      <div className="flex justify-between">
                        <span>Delivery Fee</span>
                        <span>${orderDetails?.shippingFee?.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="border-t pt-2 flex justify-between font-bold">
                      <span>Total</span>
                      <span className="text-emerald-600">
                        ${orderDetails?.total?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Support */}
              <Card
                className="animate-zoom-in"
                style={{ animationDelay: "0.2s" }}
              >
                <CardHeader>
                  <CardTitle className="text-base sm:text-lg">
                    Need Help?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2 text-xs sm:text-sm">
                    {restaurant?.phone && (
                      <div className="flex items-center space-x-2">
                        <Phone className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                        <span className="text-gray-700">
                          Call:{" "}
                          {formatCanadianPhoneNumber(restaurant?.phone || "")}
                        </span>
                      </div>
                    )}
                    {restaurant?.email && (
                      <div className="flex items-center space-x-2">
                        <Mail className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                        <span className="text-gray-700">
                          {restaurant?.email}
                        </span>
                      </div>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    className="w-full transition-all duration-300 hover:scale-105"
                  >
                    Contact Support
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Email Confirmation Notice */}
          {/* <div className="mt-6 sm:mt-8 animate-slide-in-up">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4 sm:p-6 text-center">
                <div className="text-blue-800 text-sm sm:text-base">
                  📧 A confirmation email has been sent to your email address
                  with order details and tracking information.
                </div>
              </CardContent>
            </Card>
          </div> */}
        </div>

        {/* Order Status Notification */}
        <OrderStatusNotification
          isVisible={showNotification}
          onClose={() => setShowNotification(false)}
          orderId={orderId}
          status={orderStatus as any}
          estimatedTime="30-45 min"
        />
      </div>
      <Footer />
    </>
  );
}
